import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  findDependTypeField,
  FunctionFieldType,
  FunctionIfsThenValue,
  FunctionIfsValue,
  FunctionIfValue,
  FunctionItem,
  FunctionItemDefaultValue,
  FunctionItemField,
  FunctionTableColumnValue,
  FunctionValue,
  SelectCustomValue,
  TableAdapter,
} from '@makyoapp/makyo-common'
import {
  Field,
  FieldProps,
  Label,
  LabelProps,
  SelectDropdown,
  SimpleInput,
  SingleIconSelect,
  Switch,
} from '@makyoapp/makyo-ui'

import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { ColumnOrCustomDd } from '@/components/Function/ColumnOrCustomDd'
import { ColumnOrCustomDdMulti } from '@/components/Function/ColumnOrCustomDdMulti'
import { ColumnOrCustomWithTableDd } from '@/components/Function/ColumnOrCustomWithTableDd'
import { FunctionIf } from '@/components/Function/FunctionIf'
import { FunctionIfsRepeater } from '@/components/Function/FunctionIfsRepeater'
import { FunctionIfsThenRepeater } from '@/components/Function/FunctionIfsThenRepeater'
import { SelectOrCustomDd } from '@/components/Function/SelectOrCustomDd'
import { TableDd } from '@/components/Function/TableDd'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { Help } from '@/components/Help'

import { FUNCTION_DIRECTION_OPTIONS } from '../constants'
import { FunctionDirection } from '../types'

function Lbl(props: LabelProps) {
  return <Label {...props} defaultSize={false} className={clsx('lbl-sm', props.className)} />
}

function Fld(props: FieldProps) {
  return (
    <Field
      {...props}
      defaultSize={false}
      noPad
      className={clsx(props.defaultSize !== false && 'min-h-[3rem]', props.className)}
    />
  )
}

type Props = {
  item: FunctionItem
  field: FunctionItemField
  value?: FunctionItemDefaultValue
  allValue?: FunctionValue
  onChange: (next: FunctionItemDefaultValue) => void
}

export const FnFieldSingle = ({ item, field, allValue, value, onChange }: Props) => {
  const { table, tableList, gridModal } = useGridV2Context()
  const label = <Trans i18nKey={`fn.${item.id}.fields.${field.id}.label`} />

  const columnID = gridModal?.column?.id

  if (field.hidden || !table || !tableList) {
    return null
  }

  const help = (
    <Help
      noPortal
      options={{
        placement: 'right',
      }}
    >
      <Trans i18nKey={`fn.${item.id}.fields.${field.id}.help`} />
    </Help>
  )

  const tblProp = field.tableIdDependOn
    ? tableList.find((tb) => {
        const v = allValue?.value[field?.tableIdDependOn || '']
        const vv = typeof v === 'string' ? v : (v as FunctionTableColumnValue)?.table
        return tb.id === vv
      }) ||
      ({
        id: '',
        name: '',
        columns: [],
      } as unknown as TableAdapter)
    : table

  switch (field.type) {
    case FunctionFieldType.Title: {
      return (
        <div className='py-2'>
          <div className='text-light font-bold'>{label}</div>
        </div>
      )
    }
    case FunctionFieldType.Hr: {
      return (
        <div className='py-4'>
          <hr />
        </div>
      )
    }
    case FunctionFieldType.Ifs: {
      const condValue = value as FunctionIfsValue

      return (
        <div className={clsx('py-4', 'flex flex-col gap-2')}>
          <label className='flex-c'>
            <span>{label}</span>
            {help}
          </label>
          <FunctionIfsRepeater
            id={`${item.id}-${field.id}`}
            value={condValue}
            onChange={(v) => {
              onChange(v)
            }}
            rhsCustomFieldOnly={field.rhsCustomFieldOnly}
            colOptions={table.columns}
            table={table}
            tables={tableList}
            columnID={columnID}
            defaultOperator={field.defaultIfOperator || 'AND'}
          />
        </div>
      )
    }
    case FunctionFieldType.If: {
      const condValue = value as FunctionIfValue
      return (
        <div className={clsx('py-2', 'flex flex-col gap-2')}>
          <label className='flex-c'>
            <span>{label}</span>
            {help}
          </label>
          <FunctionIf
            id={`${item.id}-${field.id}`}
            value={condValue}
            onChange={(v) => {
              onChange(v)
            }}
            colOptions={table.columns}
            table={table}
            tables={tableList}
          />
        </div>
      )
    }
    case FunctionFieldType.IfsThen: {
      const condValue = value as FunctionIfsThenValue
      return (
        <div className={clsx('py-4', 'flex flex-col gap-2')}>
          <label className='flex-c'>
            <span>{label}</span>
            {help}
          </label>
          <FunctionIfsThenRepeater
            id={`${item.id}-${field.id}`}
            value={condValue}
            onChange={(v) => {
              onChange(v)
            }}
            colOptions={table.columns}
            table={table}
            tables={tableList}
            columnID={columnID}
            label={field.ifsThenLabel}
            rhsCustomFieldOnly={field.rhsCustomFieldOnly}
          />
        </div>
      )
    }
    case FunctionFieldType.ColumnOrCustom: {
      let types = field.columnTypes ?? []
      let customValueType = field.inputType
      if (field.typeDependOn) {
        const [newColTy, newInputTy] = findDependTypeField(item, field.typeDependOn, allValue, table, tableList) || []
        if (newColTy) {
          types = newColTy
        }
        if (newInputTy) {
          customValueType = newInputTy
        }
      }
      const dd = (
        <ColumnOrCustomDd
          id={`${item.id}-${field.id}`}
          inputClassName='dark:hover:bg-slate-800!'
          inputFocusClassName='dark:bg-slate-800!'
          toggleClassName='dark:hover:bg-slate-800!'
          onChange={(v) => {
            onChange(v)
          }}
          types={types}
          withExtraOptions={customValueType === 'date' || types?.includes(ColumnTypes.DateTime) ? ['date'] : undefined}
          customValueType={customValueType}
          value={value as never}
          withCustomValue={field.withCustomValue ?? true}
          excludeColumns={columnID ? [columnID] : undefined}
          table={tblProp}
          max={field.max}
          min={field.min}
        />
      )

      return field.wrap ? (
        <Fld>
          <div className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700 w-full'>
            <div className='text-m-sm shrink-0 w-[36px] uppercase'>{label}</div>
            <div className='w-full min-w-0'>{dd}</div>
          </div>
        </Fld>
      ) : (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          {dd}
        </Fld>
      )
    }

    case FunctionFieldType.ColumnOrCustomMulti:
      return (
        <Fld isVertical>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          <ColumnOrCustomDdMulti
            id={`${item.id}-${field.id}`}
            onChange={(v) => {
              onChange(v)
            }}
            types={field.columnTypes}
            withExtraOptions={
              field.inputType === 'date' || field.columnTypes?.includes(ColumnTypes.DateTime) ? ['date'] : undefined
            }
            customValueType={field.inputType}
            value={Array.isArray(value) ? (value as never) : []}
            divClassName='justify-end'
            withCustomValue={field.withCustomValue ?? true}
            excludeColumns={columnID ? [columnID] : undefined}
            table={tblProp}
          />
        </Fld>
      )
    case FunctionFieldType.ColumnOrCustomWithTable: {
      return (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          <ColumnOrCustomWithTableDd
            id={`${item.id}-${field.id}`}
            onChange={(v) => {
              onChange(v)
            }}
            types={field.columnTypes}
            value={value as FunctionTableColumnValue}
            excludeColumns={columnID ? [columnID] : undefined}
            tables={tableList}
          />
        </Fld>
      )
    }
    case FunctionFieldType.Table: {
      return (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          <div className='w-full min-w-0'>
            <TableDd
              id={`${item.id}-${field.id}`}
              onChange={(v) => {
                onChange(v)
              }}
              value={value as string}
              tables={tableList}
            />
          </div>
        </Fld>
      )
    }
    case FunctionFieldType.SelectCustom: {
      const v = value as SelectCustomValue
      return (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          <SelectOrCustomDd
            id={`${item.id}-${field.id}`}
            value={
              v ||
              field.defaultValue || {
                isCustom: true,
                value: '',
              }
            }
            options={field.options as never}
            onChange={(v) => {
              onChange(v)
            }}
          />
        </Fld>
      )
    }
    case FunctionFieldType.Select: {
      const dependValue = allValue?.value[field?.optionsFilterDependOn || '']
      const hasOptionsFilter = Boolean(field.optionsFilterDependOn) && Boolean(field.optionsFilter) && dependValue

      let filteredOptions = field.options || []

      if (hasOptionsFilter) {
        filteredOptions = field.optionsFilter?.(field.options || [], dependValue || '') || []
      }
      return (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span>{label}</span>
            {help}
          </Lbl>
          <div className='w-full min-w-0'>
            <SelectDropdown
              menuClassName='min-w-[260px] text-m-m'
              menuZIndex='z-100'
              portalMenu
              onChange={(opt) => {
                onChange((opt as unknown as { value: number | string }).value)
              }}
              menuFullWidth={false}
              withSearch={(field.options || []).length > SHOW_SEARCH_LIMIT}
              multiple={field.multiple}
              menuHeadingClassName='text-light uppercase'
              value={
                (field.multiple
                  ? filteredOptions
                      .filter((o) => {
                        return Array.isArray(value) ? value.includes(o.value as never) : []
                      })
                      .map((o) => o.value)
                  : filteredOptions.find((o) => {
                      if (Array.isArray(value) && field.multiple === true) {
                        return undefined
                      } else {
                        return o.value === (value as string)
                      }
                    })) as unknown as FunctionItem
              }
              options={filteredOptions as unknown as FunctionItem[]}
              optionLabel={
                // @ts-ignore
                (opt) => `${opt?.label} ${opt?.value}`
              }
              itemClassName='mk-ag-sdd'
              renderOption={(opt) => {
                // @ts-ignore
                return opt?.label || opt?.value
              }}
              {...field.selectProps}
            />
          </div>
        </Fld>
      )
    }
    case FunctionFieldType.Input:
      return (
        <Fld>
          <Lbl className='flex-c shrink-0! grow-0!'>
            <span className='inline-block'>{label}</span>
            {help}
          </Lbl>
          <div className='w-full min-w-0'>
            <SimpleInput
              inputClassName='min-w-0'
              type={field.inputType || 'text'}
              value={value as string}
              onChange={(e) => {
                const v = e.currentTarget.value
                onChange(v)
              }}
            />
            {field.isRegex && (
              <div className='text-m-sm'>
                <a href='https://autoregex.xyz' target='_blank' rel='noreferrer' className='link'>
                  <Trans>Generate Regex</Trans>
                </a>
              </div>
            )}
          </div>
        </Fld>
      )
    case FunctionFieldType.Switch:
      return field.id.includes('start_from_first_row') ? null : (
        <Fld isVertical={false}>
          <label className='flex-c w-full'>
            <span>{label}</span>
            {help}
          </label>
          <div>
            <Switch
              checked={value as boolean}
              onClick={(isChecked) => {
                onChange(isChecked)
              }}
            />
          </div>
        </Fld>
      )
    case FunctionFieldType.Direction:
      return (
        <Fld isVertical={false}>
          <label className='flex-c w-full'>
            <span>{label}</span>
            {help}
          </label>
          <div>
            <SingleIconSelect
              onClick={(opt) => {
                onChange(opt.id)
              }}
              options={FUNCTION_DIRECTION_OPTIONS}
              selected={FUNCTION_DIRECTION_OPTIONS.find((opt) => opt.id === value)?.id || FunctionDirection.Horizontal}
            />
          </div>
        </Fld>
      )
    default:
      return <div></div>
  }
}
